import { FormEvent, useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import ApiService, { setSecurityHandlers } from "@/services/ApiService";

export default function SecurityDialogs() {
  const [mode, setMode] = useState<"reason" | "reauth" | null>(null);
  const [reason, setReason] = useState("");
  const [password, setPassword] = useState("");
  const [otp, setOtp] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState<{
    resolve: (value?: string) => void;
    reject: (error: Error) => void;
  } | null>(null);
  useEffect(() => {
    setSecurityHandlers({
      reason: () =>
        new Promise((resolve, reject) => {
          setPending({ resolve, reject });
          setMode("reason");
          setError("");
        }),
      reauth: () =>
        new Promise<void>((resolve, reject) => {
          setPending({ resolve: () => resolve(), reject });
          setMode("reauth");
          setError("");
        }),
    });
    return () => setSecurityHandlers(null);
  }, []);
  const cancel = () => {
    pending?.reject(new Error("操作をキャンセルしました。"));
    setMode(null);
    setPending(null);
    setPassword("");
    setOtp("");
  };
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      if (mode === "reauth")
        await ApiService.post("auth/reauthenticate", {
          password,
          otp: otp || null,
        });
      else sessionStorage.setItem("job-admin-reason", reason.trim());
      pending?.resolve(mode === "reason" ? reason.trim() : undefined);
      setMode(null);
      setPending(null);
      setPassword("");
      setOtp("");
    } catch (failure) {
      setError(failure.response?.data?.message ?? "認証に失敗しました。");
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog
      open={!!mode}
      onOpenChange={(open) => {
        if (!open && !busy) cancel();
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {mode === "reason" ? "管理操作の理由" : "本人確認"}
          </DialogTitle>
          <DialogDescription>
            {mode === "reason"
              ? "操作理由を監査ログに記録します。このセッションの管理操作に使用します。"
              : "個人情報の閲覧や権限変更の前に、パスワードを再入力してください。"}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          {mode === "reason" ? (
            <label className="block">
              理由
              <Input
                required
                minLength={3}
                maxLength={500}
                autoFocus
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
            </label>
          ) : (
            <>
              <label className="block">
                パスワード
                <Input
                  required
                  autoFocus
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </label>
              <label className="block">
                認証コード / リカバリーコード（MFA有効時）
                <Input
                  autoComplete="one-time-code"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value)}
                />
              </label>
            </>
          )}
          {error && (
            <p role="alert" className="text-destructive">
              {error}
            </p>
          )}
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              disabled={busy}
              onClick={cancel}
            >
              キャンセル
            </Button>
            <Button disabled={busy}>確認</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
